import type { PageBlock } from "@/utils/customPageStore";
import AdminEditField from "@/components/feature/AdminEditField";
import { SectionAdminBar, useBlockConfig } from "./shared";

interface ArtistHeadingBlockProps {
  block: PageBlock;
  isAdmin: boolean;
  isDark: boolean;
  headingFont: string;
  headingStyle: React.CSSProperties;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  onUpdate?: (b: PageBlock) => void;
}

export default function ArtistHeadingBlock({
  block,
  isAdmin,
  isDark,
  headingFont,
  headingStyle,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  onUpdate,
}: ArtistHeadingBlockProps) {
  const { get, setConfig } = useBlockConfig(block, onUpdate);
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-600";

  const eyebrow = get("eyebrow", "");
  const heading = get("heading", "");
  const subheading = get("subheading", "");
  const seeMoreLabel = get("seeMoreLabel", "See All Our Artists");
  const seeMoreUrl = get("seeMoreUrl", "/artists");

  return (
    <div className={`mb-8 ${isAdmin ? "rounded-xl overflow-hidden border border-green-200" : ""}`}>
      {isAdmin && (
        <SectionAdminBar
          layout="artist-heading"
          isDark={isDark}
          onDelete={onDelete}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          isFirst={isFirst}
          isLast={isLast}
        />
      )}
      <div className="py-12 px-6 text-center rounded-2xl" style={{ background: isDark ? "#1f2937" : "#fff" }}>
        {eyebrow && (
          <AdminEditField value={eyebrow} onSave={(v) => setConfig("eyebrow", v)} inputClassName="text-center text-sm">
            <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{eyebrow}</span>
          </AdminEditField>
        )}
        {heading && (
          <AdminEditField value={heading} onSave={(v) => setConfig("heading", v)} inputClassName="text-center text-3xl md:text-4xl font-extrabold">
            <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-3 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
              {heading}
            </h2>
          </AdminEditField>
        )}
        {subheading && (
          <AdminEditField value={subheading} onSave={(v) => setConfig("subheading", v)} inputClassName="text-center">
            <p className={`text-base max-w-2xl mx-auto ${subColor}`}>{subheading}</p>
          </AdminEditField>
        )}

        <div className="flex flex-col items-center gap-1.5 mt-6">
          {isAdmin && onUpdate ? (
            <>
              <AdminEditField
                value={seeMoreLabel}
                onSave={(v) => setConfig("seeMoreLabel", v)}
                inputClassName="text-center font-bold"
              >
                <div className="bg-pink-500 text-white font-bold px-8 py-3 rounded-full whitespace-nowrap">
                  {seeMoreLabel}
                </div>
              </AdminEditField>
              <AdminEditField
                value={seeMoreUrl}
                onSave={(v) => setConfig("seeMoreUrl", v)}
                inputClassName="text-xs text-center text-gray-400"
              >
                <span className="text-xs text-gray-400">URL: {seeMoreUrl}</span>
              </AdminEditField>
            </>
          ) : (
            <a
              href={seeMoreUrl}
              className="mt-6 bg-pink-500 hover:bg-pink-600 text-white font-bold px-8 py-3 rounded-full cursor-pointer whitespace-nowrap transition-colors"
            >
              {seeMoreLabel}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}